import React, { useCallback, useEffect, useRef, useState } from 'react';

export type DashboardTheme = 'light' | 'dark';

type Props = {
    html: string;
    title?: string;
    className?: string;
    theme?: DashboardTheme;
};

/**
 * Sandboxed iframe for AI-generated collect dashboards.
 * Height follows content via postMessage; theme synced from parent app.
 */
const CollectDashboardFrame = ({
    html,
    title = 'Dashboard',
    className = '',
    theme = 'light',
}: Props) => {
    const [frameHeight, setFrameHeight] = useState(420);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const lastHtmlRef = useRef(html);

    useEffect(() => {
        if (lastHtmlRef.current !== html) {
            lastHtmlRef.current = html;
            setFrameHeight(420);
        }
    }, [html]);

    const postTheme = useCallback(() => {
        const win = iframeRef.current?.contentWindow;
        if (!win) return;
        const msg = { type: 'famla-dashboard-theme', theme };
        win.postMessage(msg, '*');
        [100, 400, 1000].forEach((ms) => {
            window.setTimeout(() => win.postMessage(msg, '*'), ms);
        });
    }, [theme]);

    useEffect(() => {
        postTheme();
    }, [postTheme, html, frameHeight]);

    useEffect(() => {
        const onMessage = (event: MessageEvent) => {
            const data = event.data as { type?: string; height?: number } | null;
            if (!data || data.type !== 'famla-dashboard-resize') return;
            const h = Number(data.height);
            if (!Number.isFinite(h) || h < 100) return;
            setFrameHeight(Math.min(Math.ceil(h) + 8, 5000));
        };
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, []);

    const onLoad = () => {
        postTheme();
    };

    return (
        <iframe
            ref={iframeRef}
            title={title}
            className={`collect-dashboard-iframe w-100 border-0 ${className}`.trim()}
            sandbox="allow-scripts"
            srcDoc={html}
            scrolling="no"
            onLoad={onLoad}
            style={{
                height: frameHeight,
                minHeight: 0,
                display: 'block',
                width: '100%',
                background: theme === 'dark' ? '#0E0F0E' : '#FFFFFF',
                overflow: 'hidden',
            }}
        />
    );
};

export default CollectDashboardFrame;
